import {Component, signal} from '@angular/core';
import {v4 as uuidv4} from 'uuid';
import {MessageService} from 'primeng/api';
import {UploadService} from './upload.service';
import {UploadItem} from './upload.models';
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {FileUploadModule} from 'primeng/fileupload';
import {ProgressBarModule} from 'primeng/progressbar';
import {ButtonModule} from 'primeng/button';
import {ToastModule} from 'primeng/toast';
import {DecimalPipe, NgClass, NgForOf, NgIf} from '@angular/common';
import {Card} from 'primeng/card';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  providers: [MessageService],
  standalone: true,
  imports: [FileUploadModule, ProgressBarModule, ButtonModule, ToastModule, NgClass, DecimalPipe, NgIf, NgForOf, Card]
})
export class UploadComponent {
  items = signal<UploadItem[]>([]);
  private subs = new Map<string, Subscription>();

  maxSizeMB = 10;
  allowedTypes = new Set([
    'text/csv'
  ]);

  constructor(
    private uploadService: UploadService,
    private toast: MessageService
  ) {
  }

  onSelect(event: any) {
    const files: File[] = event.files ?? [];
    const newItems: UploadItem[] = [];

    for (const file of files) {
      const validation = this.validateFile(file);
      if (validation !== true) {
        this.toast.add({severity: 'warn', summary: 'File refused', detail: validation});
        continue;
      }
      newItems.push({
        id: uuidv4(),
        file,
        status: 'queued',
        progress: 0
      });
    }

    if (newItems.length) {
      this.items.update(list => [...list, ...newItems]);
    }
  }

  canUpload(): boolean {
    return this.items().some(i => !['uploading', 'done'].includes(i.status));
  }

  startUploadAll(): void {
    this.items().forEach(i => this.startUpload(i.id));
  }

  startUpload(id: string) {
    const item = this.items().find(x => x.id === id);
    if (!item || ['uploading', 'done'].includes(item.status)) {
      return;
    }

    this.updateItem(id, {status: 'uploading', progress: 0, errorMessage: undefined});

    this.uploadService.getPresignedUrl({
      fileName: item.file.name,
      contentType: item.file.type,
      size: item.file.size
    }).subscribe({
      next: presign => {
        this.updateItem(id, {s3Key: presign.key});

        const sub = this.uploadService.uploadToS3(presign, item.file)
          .pipe(finalize(() => this.subs.delete(id)))
          .subscribe({
            next: progress => this.updateItem(id, {progress}),
            error: err => {
              const msg = this.extractError(err);
              this.updateItem(id, {status: 'error', errorMessage: msg});
              this.toast.add({severity: 'error', summary: 'Upload failed', detail: msg});
            },
            complete: () => {
              this.updateItem(id, {status: 'done', progress: 100});
              this.toast.add({severity: 'success', summary: 'Upload completed', detail: item.file.name});
            }
          });

        this.subs.set(id, sub);
      },
      error: err => {
        const msg = this.extractError(err);
        this.updateItem(id, {status: 'error', errorMessage: msg});
        this.toast.add({severity: 'error', summary: 'Presign failed', detail: msg});
      }
    });
  }

  cancelUpload(id: string) {
    const sub = this.subs.get(id);
    if (sub) {
      sub.unsubscribe();
    }
    this.subs.delete(id);
    this.updateItem(id, {status: 'canceled', errorMessage: undefined});
  }

  retryUpload(id: string) {
    this.startUpload(id);
  }

  removeItem(id: string) {
    this.cancelUpload(id);
    this.items.update(list => list.filter(x => x.id !== id));
  }

  canClearAll(): boolean {
    return this.items().every(i => !['uploading'].includes(i.status));
  }

  clearAll() {
    this.items().forEach(i => this.cancelUpload(i.id));
    this.items.set([]);
  }

  private validateFile(file: File): true | string {
    const maxBytes = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return `Max size ${this.maxSizeMB} MB.`;
    }
    if (file.type && !this.allowedTypes.has(file.type)) {
      return `File type not allowed (${file.type}).`;
    }
    return true;
  }

  private updateItem(id: string, patch: Partial<UploadItem>) {
    this.items.update(list =>
      list.map(x => x.id === id ? {...x, ...patch} : x)
    );
  }

  private extractError(err: any): string {
    console.error('Upload error', err);
    return err?.error?.message || err?.error || err?.statusText || 'Unknown error';
  }
}
